import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card, Input, Spinner,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@agion/ui'
import { History, Search } from 'lucide-react'
import { listAuditLogs } from '@/features/audit/infra_audit.service'
import type { AuditLogEntry } from '@/core/infra_types'
import { formatDate } from '@/core/infra_utils'

export function AuditPage() {
  const { t } = useTranslation(['pages'])
  const [entries, setEntries] = useState<AuditLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const loadAuditLogs = useCallback(async () => {
    try {
      setIsLoading(true)
      const result = await listAuditLogs({ perPage: 100 })
      setEntries(Array.isArray(result.data) ? result.data : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadAuditLogs()
  }, [loadAuditLogs])

  const filtered = entries.filter(entry =>
    entry.actorEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.resourceType.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="container-standard">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <History className="text-blue-600" size={28} />
          <div>
            <h1 className="page-title">{t('pages:audit.title', 'Audit Log')}</h1>
            <p className="page-description">
              {t('pages:audit.description', 'Track all infrastructure actions and changes')}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t('pages:audit.search', 'Search by actor, action, or resource...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Card>
          <div className="p-8 text-center text-red-600">{error}</div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {t('pages:audit.empty', 'No audit log entries found.')}
          </div>
        </Card>
      ) : (
        <Table variant="modern">
          <TableHeader variant="modern">
            <TableRow variant="modern">
              <TableHead variant="modern">{t('pages:audit.columns.timestamp', 'Timestamp')}</TableHead>
              <TableHead variant="modern">{t('pages:audit.columns.actor', 'Actor')}</TableHead>
              <TableHead variant="modern">{t('pages:audit.columns.action', 'Action')}</TableHead>
              <TableHead variant="modern">{t('pages:audit.columns.resource', 'Resource')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody variant="modern">
            {filtered.map(entry => (
              <TableRow key={entry.id} variant="modern">
                <TableCell className="text-slate-500 text-xs whitespace-nowrap">
                  {formatDate(entry.createdAt)}
                </TableCell>
                <TableCell className="truncate max-w-[200px]">
                  {entry.actorEmail}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {entry.action}
                </TableCell>
                <TableCell className="text-slate-600 dark:text-slate-400 truncate max-w-[200px]">
                  {entry.resourceType}
                  {entry.resourceId ? ` / ${entry.resourceId}` : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
